import { useEffect } from "react";
import {
  ADMIN_ACCOUNT_DOMAIN_PARAM,
  isKnitKnotEmail,
} from "@knitknot/shared/constants";
import { useWorkspaceStore } from "@/hooks/state";
import type { Workspace } from "@/services/workspace/workspaceService";

export function workspaceForAccountDomain(
  workspaces: readonly Workspace[],
  accountDomain: string | null,
): Workspace | null {
  const normalized = accountDomain?.trim().toLowerCase();
  if (!normalized) return null;
  return (
    workspaces.find(
      (workspace) => workspace.account?.domain?.trim().toLowerCase() === normalized,
    ) ?? null
  );
}

/**
 * Superadmin "view as" entry point. The internal admin dashboard
 * (internal.knitknot.ai) deep-links here with either ?admin_workspace=<id> or
 * ?admin_account_domain=<domain>. The domain form resolves against the current
 * environment's workspace list, then both forms adopt that workspace as the
 * active tenant (cookie + store), strip the param, and hard-reload so every
 * data hook refetches against it.
 *
 * No-op for non-superadmins (param is just stripped) and on normal loads.
 */
export function useAdminWorkspaceAdoption(
  workspaces: readonly Workspace[],
  workspacesLoading: boolean,
) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasWorkspace = params.has("admin_workspace");
    const hasAccountDomain = params.has(ADMIN_ACCOUNT_DOMAIN_PARAM);
    if (!hasWorkspace && !hasAccountDomain) return;

    // Keep the handoff in the URL while the authenticated workspace context loads.
    // UUID links also depend on staff identity being available before adoption.
    if (workspacesLoading) return;

    const target = hasWorkspace
      ? params.get("admin_workspace")
      : workspaceForAccountDomain(workspaces, params.get(ADMIN_ACCOUNT_DOMAIN_PARAM))?.id;
    const store = useWorkspaceStore.getState();
    // In local dev, the console and internal app share the JWT cookie across
    // localhost ports but not localStorage. The email-domain check lets that
    // already-authenticated staff session complete this UI handoff; every
    // workspace API still authorizes the user server-side.
    const mayAdopt =
      store.isSuperadmin ||
      isKnitKnotEmail(store.userInfo?.email);
    if (target && mayAdopt) {
      store.setWorkspaceId(target);
    }

    // Always strip the param (even if empty/non-admin) so it never lingers.
    params.delete("admin_workspace");
    params.delete(ADMIN_ACCOUNT_DOMAIN_PARAM);
    const qs = params.toString();
    window.location.replace(window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
  }, [workspaces, workspacesLoading]);
}
